import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api, { resolveUploadUrl } from '../../config/api';
import { FaNewspaper, FaHandshake, FaTicketAlt, FaShoppingCart, FaUsers, FaUserCheck, FaUserPlus, FaIdCard, FaExclamationTriangle, FaEnvelope, FaImage, FaTrash, FaMoneyBillWave, FaFileExcel, FaPaperPlane, FaHistory, FaClipboardList, FaSpinner } from 'react-icons/fa';
import { MEMBERSHIP_SEASON } from '../../config/membershipSeason';
import './AdminDashboard.css';

const QUICK_ADDRESS_EXPORTS = [
  { id: 'active_address_all', label: 'Tous les membres actifs' },
  { id: 'active_address_suisse_jofc_adult', label: 'Suisse — JOFC Adult' },
  { id: 'active_address_suisse_jofc_u14', label: 'Suisse — JOFC U14' },
  { id: 'active_address_italie_jofc_adult', label: 'Italie — JOFC Adult' },
  { id: 'active_address_italie_jofc_u14', label: 'Italie — JOFC U14' },
  { id: 'active_address_france_jofc_adult', label: 'France — JOFC Adult' },
  { id: 'active_address_france_jofc_u14', label: 'France — JOFC U14' },
  { id: 'active_address_belgique', label: 'Adresse en Belgique' },
];

const QUICK_LINK_CATEGORIES = [
  {
    title: 'Billets et participations',
    links: [
      { to: '/admin/tickets', label: 'Gérer les billets' },
      { to: '/admin/ticket-orders', label: 'Voir les commandes' },
      { to: '/admin/ticket-participations', label: 'Voir les participations', icon: FaClipboardList },
      { to: '/admin/ticket-opponents', label: 'Équipes adverses par compétition' },
    ],
  },
  {
    title: 'Gestion utilisateurs',
    links: [
      { to: '/admin/users', label: 'Gérer les utilisateurs' },
      { to: '/admin/member-season-codes', label: 'Codes PNR / e-card / J-card par saison', icon: FaIdCard },
      { to: '/admin/export-members', label: 'Exports membres (Excel)', icon: FaFileExcel },
      { to: '/admin/register-member', label: 'Inscrire un nouveau membre' },
      { to: '/admin/new-members', label: 'Valider nouveaux membres' },
    ],
  },
  {
    title: 'Mailing et newsletters',
    links: [
      { to: '/admin/mailing-auto', label: 'Mailing automatique', icon: FaPaperPlane },
      { to: '/admin/email-templates', label: "Modèles d'emails", icon: FaEnvelope },
      { to: '/admin/email-history', label: 'Historique des e-mails envoyés', icon: FaHistory },
      { to: '/admin/subscribers', label: 'Envoyer des emails' },
    ],
  },
  {
    title: 'Contenu',
    links: [
      { to: '/admin/news', label: 'Gérer les news' },
      { to: '/admin/partners', label: 'Gérer les partenaires' },
      { to: '/admin/pricing', label: 'Tarifs (paiement et comptes)', icon: FaMoneyBillWave },
    ],
  },
  {
    title: 'Juventus Card',
    links: [
      { to: '/admin/juventus-card', label: 'Demandes Juventus Card' },
      { to: '/admin/juventus-card-lost', label: 'Déclarations de perte Juventus Card' },
    ],
  },
];

const AdminDashboard = () => {
  const [heroImageUrl, setHeroImageUrl] = useState(null);
  const [heroLoading, setHeroLoading] = useState(true);
  const [heroUploading, setHeroUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [registerSociImageUrl, setRegisterSociImageUrl] = useState(null);
  const [registerSociLoading, setRegisterSociLoading] = useState(true);
  const [registerSociUploading, setRegisterSociUploading] = useState(false);
  const registerSociFileInputRef = useRef(null);

  const [stats, setStats] = useState({
    newsCount: 0,
    partnersCount: 0,
    ticketsCount: 0,
    ordersCount: 0,
    usersCount: 0,
    membersCount: 0,
    newMembersCount: 0,
    juventusCardCount: 0,
    juventusCardLostCount: 0
  });
  const [loading, setLoading] = useState(true);
  const [exportingAddressId, setExportingAddressId] = useState(null);
  const [addressExportError, setAddressExportError] = useState('');

  useEffect(() => {
    fetchStats();
    fetchHero();
    fetchRegisterSociImage();
  }, []);

  const fetchHero = async () => {
    try {
      setHeroLoading(true);
      const res = await api.get('/settings/hero');
      setHeroImageUrl(res.data.heroImageUrl);
    } catch (err) {
      console.error('Error fetching hero:', err);
    } finally {
      setHeroLoading(false);
    }
  };

  const handleHeroUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setHeroUploading(true);
      const formData = new FormData();
      formData.append('image', file);
      const res = await api.put('/settings/hero', formData);
      setHeroImageUrl(res.data.heroImageUrl);
      alert('Image hero mise à jour');
    } catch (err) {
      console.error('Error uploading hero:', err);
      alert(err.response?.data?.error || 'Erreur lors de l\'upload');
    } finally {
      setHeroUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleHeroReset = async () => {
    if (!window.confirm('Réinitialiser l\'image hero par défaut ?')) return;
    try {
      await api.delete('/settings/hero');
      setHeroImageUrl(null);
      alert('Image hero réinitialisée');
    } catch (err) {
      console.error('Error resetting hero:', err);
      alert('Erreur lors de la réinitialisation');
    }
  };

  const fetchRegisterSociImage = async () => {
    try {
      setRegisterSociLoading(true);
      const res = await api.get('/settings/register-soci-image');
      setRegisterSociImageUrl(res.data.registerSociImageUrl);
    } catch (err) {
      console.error('Error fetching register soci image:', err);
    } finally {
      setRegisterSociLoading(false);
    }
  };

  const handleRegisterSociUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setRegisterSociUploading(true);
      const formData = new FormData();
      formData.append('image', file);
      const res = await api.put('/settings/register-soci-image', formData);
      setRegisterSociImageUrl(res.data.registerSociImageUrl);
      alert('Image inscription (carte Soci) mise à jour');
    } catch (err) {
      console.error('Error uploading register soci image:', err);
      alert(err.response?.data?.error || 'Erreur lors de l\'upload');
    } finally {
      setRegisterSociUploading(false);
      if (registerSociFileInputRef.current) registerSociFileInputRef.current.value = '';
    }
  };

  const handleRegisterSociReset = async () => {
    if (!window.confirm('Réinitialiser l\'image « Soci » sur le formulaire d\'inscription (image par défaut du site) ?')) return;
    try {
      await api.delete('/settings/register-soci-image');
      setRegisterSociImageUrl(null);
      alert('Image réinitialisée');
    } catch (err) {
      console.error('Error resetting register soci image:', err);
      alert('Erreur lors de la réinitialisation');
    }
  };

  const fetchStats = async () => {
    try {
      setLoading(true);
      const [news, partners, tickets, orders, users, newMembers, juventusCard, juventusCardLost] = await Promise.all([
        api.get('/news/admin/list'),
        api.get('/partners'),
        api.get('/tickets'),
        api.get('/ticket-orders').catch(() => ({ data: [] })),
        api.get('/users'),
        api.get('/users/new-members').catch(() => ({ data: [] })),
        api.get('/juventus-card-requests').catch(() => ({ data: [] })),
        api.get('/juventus-card-lost').catch(() => ({ data: [] }))
      ]);

      setStats({
        newsCount: Array.isArray(news.data.news) ? news.data.news.length : 0,
        partnersCount: partners.data.length || 0,
        ticketsCount: tickets.data.length || 0,
        ordersCount: orders.data.length || 0,
        usersCount: users.data.length || 0,
        membersCount: users.data.filter(u => u.isMember).length || 0,
        newMembersCount: newMembers.data.length || 0,
        juventusCardCount: Array.isArray(juventusCard.data) ? juventusCard.data.length : 0,
        juventusCardLostCount: Array.isArray(juventusCardLost.data) ? juventusCardLost.data.length : 0
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadAddressExport = useCallback(async (type) => {
    try {
      setExportingAddressId(type);
      setAddressExportError('');
      const params = new URLSearchParams({ type, season: MEMBERSHIP_SEASON });
      const response = await api.get(`/users/export-members?${params.toString()}`, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cd = response.headers['content-disposition'];
      let filename = `export-membres-${type}.xlsx`;
      if (cd) {
        const m = /filename="?([^";]+)"?/i.exec(cd);
        if (m && m[1]) filename = m[1];
      }
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Address export failed:', error);
      let msg = "Échec du téléchargement.";
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const parsed = JSON.parse(text);
          if (parsed.message) msg = parsed.message;
          if (parsed.error) msg = parsed.error;
        } catch {
          /* ignore */
        }
      } else if (error.response?.data?.error) {
        msg = error.response.data.error;
      }
      setAddressExportError(msg);
    } finally {
      setExportingAddressId(null);
    }
  }, []);

  const statCards = [
    { title: 'News', count: stats.newsCount, icon: <FaNewspaper />, link: '/admin/news', color: '#3498db' },
    { title: 'Partenaires', count: stats.partnersCount, icon: <FaHandshake />, link: '/admin/partners', color: '#2ecc71' },
    { title: 'Billets', count: stats.ticketsCount, icon: <FaTicketAlt />, link: '/admin/tickets', color: '#e74c3c' },
    { title: 'Commandes', count: stats.ordersCount, icon: <FaShoppingCart />, link: '/admin/ticket-orders', color: '#16a085' },
    { title: 'Utilisateurs', count: stats.usersCount, icon: <FaUsers />, link: '/admin/users', color: '#f39c12' },
    { title: 'Membres actifs', count: stats.membersCount, icon: <FaUserCheck />, link: '/admin/subscribers', color: '#9b59b6' },
    { title: 'Nouveaux membres', count: stats.newMembersCount, icon: <FaUserPlus />, link: '/admin/new-members', color: '#e67e22' },
    { title: 'Demandes Juventus Card', count: stats.juventusCardCount, icon: <FaIdCard />, link: '/admin/juventus-card', color: '#1a472a' },
    { title: 'Déclarations de perte Juventus Card', count: stats.juventusCardLostCount, icon: <FaExclamationTriangle />, link: '/admin/juventus-card-lost', color: '#c0392b' }
  ];

  return (
    <div className="admin-dashboard">
      <div className="admin-container">
        <div className="admin-header">
          <h1>Tableau de bord</h1>
          <p>Bienvenue dans l'interface d'administration</p>
        </div>

        <div className="quick-links">
          <h2>Actions rapides</h2>
          {QUICK_LINK_CATEGORIES.map((category) => (
            <section key={category.title} className="quick-links-category">
              <h3>{category.title}</h3>
              <div className="links-grid">
                {category.links.map((link) => {
                  const Icon = link.icon;
                  return (
                    <Link key={link.to} to={link.to} className="quick-link">
                      {Icon ? <Icon style={{ marginRight: '0.5rem' }} aria-hidden /> : null}
                      {link.label}
                    </Link>
                  );
                })}
              </div>
            </section>
          ))}
        </div>

        {loading ? (
          <div className="loading">Chargement...</div>
        ) : (
          <div className="stats-grid">
            {statCards.map((card, index) => (
              <Link to={card.link} key={index} className="stat-card" style={{ borderTopColor: card.color }}>
                <div className="stat-icon" style={{ color: card.color }}>
                  {card.icon}
                </div>
                <div className="stat-content">
                  <h3>{card.count}</h3>
                  <p>{card.title}</p>
                </div>
              </Link>
            ))}
          </div>
        )}

        <div className="hero-settings">
          <h2>Image hero (page d'accueil)</h2>
          <div className="hero-settings-content">
            <div className="hero-preview">
              {heroLoading ? (
                <div className="hero-placeholder">Chargement...</div>
              ) : heroImageUrl ? (
                <img
                  src={resolveUploadUrl(heroImageUrl)}
                  alt="Hero actuel"
                  className="hero-preview-img"
                />
              ) : (
                <div className="hero-placeholder">Image par défaut</div>
              )}
            </div>
            <div className="hero-actions">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleHeroUpload}
                style={{ display: 'none' }}
              />
              <button
                type="button"
                className="btn-hero-upload"
                onClick={() => fileInputRef.current?.click()}
                disabled={heroUploading}
              >
                <FaImage /> {heroUploading ? 'Envoi...' : 'Changer l\'image hero'}
              </button>
              {heroImageUrl && (
                <button
                  type="button"
                  className="btn-hero-reset"
                  onClick={handleHeroReset}
                >
                  <FaTrash /> Réinitialiser (image par défaut)
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="hero-settings register-soci-settings">
          <h2>Image « Soci » (formulaire d&apos;inscription)</h2>
          <p className="register-soci-settings-intro">
            Bannière affichée sur la page d&apos;inscription (bloc <code>register-soci-image</code>). Si aucune image n&apos;est définie, l&apos;image fournie avec le site est utilisée.
          </p>
          <div className="hero-settings-content">
            <div className="hero-preview">
              {registerSociLoading ? (
                <div className="hero-placeholder">Chargement...</div>
              ) : registerSociImageUrl ? (
                <img
                  src={resolveUploadUrl(registerSociImageUrl)}
                  alt="Aperçu bannière Soci — inscription"
                  className="hero-preview-img register-soci-preview-img"
                />
              ) : (
                <div className="hero-placeholder">Image du site par défaut (non personnalisée)</div>
              )}
            </div>
            <div className="hero-actions">
              <input
                ref={registerSociFileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleRegisterSociUpload}
                style={{ display: 'none' }}
              />
              <button
                type="button"
                className="btn-hero-upload"
                onClick={() => registerSociFileInputRef.current?.click()}
                disabled={registerSociUploading}
              >
                <FaImage /> {registerSociUploading ? 'Envoi...' : 'Changer l\'image'}
              </button>
              {registerSociImageUrl && (
                <button
                  type="button"
                  className="btn-hero-reset"
                  onClick={handleRegisterSociReset}
                >
                  <FaTrash /> Réinitialiser (défaut site)
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="quick-address-exports">
          <h2>Exports membres actifs — adresses ({MEMBERSHIP_SEASON})</h2>
          <p className="quick-address-exports-intro">
            Téléchargement Excel avec date d&apos;activation, civilité, nom, prénom, adresse et mobile
            pour la saison en cours.
          </p>
          {addressExportError && (
            <div className="quick-address-exports-error">{addressExportError}</div>
          )}
          <div className="quick-address-exports-grid">
            {QUICK_ADDRESS_EXPORTS.map((item) => (
              <button
                key={item.id}
                type="button"
                className="quick-address-export-btn"
                onClick={() => downloadAddressExport(item.id)}
                disabled={!!exportingAddressId}
              >
                {exportingAddressId === item.id ? (
                  <>
                    <FaSpinner className="spin" /> Export…
                  </>
                ) : (
                  <>
                    <FaFileExcel aria-hidden /> {item.label}
                  </>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;


